import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { API_ORIGIN } from '../../api/baseUrl';
import OnboardingChecklist from '../../components/OnboardingChecklist';

// ============================================================================
// TYPES
// ============================================================================

interface OverallStats {
  total_employees: number;
  active_employees: number;
  total_teams: number;
  total_customers: number;
  total_products: number;
  total_reports_all_time: number;
  total_reports_this_year: number;
  total_reports_this_month: number;
}

interface ThisWeekStats {
  week_number: number;
  year: number;
  week_label: string;
  week_start: string;
  week_end: string;
  reports_submitted: number;
  unique_reporters: number;
  rollups_completed: number;
  rollups_pending: number;
  managers_with_rollups: string[];
  managers_without_rollups: string[];
}

interface DashboardData {
  overall: OverallStats;
  this_week: ThisWeekStats;
}

// ============================================================================
// API
// ============================================================================

async function getDashboardData(): Promise<DashboardData> {
  const res = await fetch(`${API_ORIGIN}/api/command-center/dashboard`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error);
  return json.data;
}

// ============================================================================
// COMPONENTS
// ============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo';
}

function StatCard({ title, value, subtitle, icon, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl border ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{title}</div>
          {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

function QuickAction({ href, icon, label }: { href: string; icon: string; label: string }) {
  return (
    <a
      href={href}
      className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-center"
    >
      <div className="text-2xl mb-2">{icon}</div>
      <div className="text-sm font-medium text-gray-700">{label}</div>
    </a>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdminDashboard() {
  const { user } = useAdminAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['command-center', 'dashboard'],
    queryFn: getDashboardData,
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Failed to load dashboard data. Please try again.
      </div>
    );
  }

  const overall = data?.overall;
  const thisWeek = data?.this_week;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Command Center</h1>
        <p className="text-gray-500 mt-1">
          Welcome back, {user?.first_name || user?.email?.split('@')[0] || 'Admin'}
        </p>
      </div>

      {/* First-run onboarding checklist (Phase 4 Action 46). Self-hides
          when the tenant has the flag off OR every item is complete. */}
      <OnboardingChecklist />

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* OVERALL STATUS */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span>📊</span> Overall Status
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Employees"
            value={overall?.total_employees || 0}
            subtitle={overall?.active_employees !== overall?.total_employees
              ? `${overall?.active_employees || 0} active`
              : undefined}
            icon="👥"
            color="blue"
          />
          <StatCard
            title="Teams"
            value={overall?.total_teams || 0}
            icon="🏠"
            color="purple"
          />
          <StatCard
            title="Customers"
            value={overall?.total_customers || 0}
            icon="🏢"
            color="green"
          />
          <StatCard
            title="Products"
            value={overall?.total_products || 0}
            icon="📦"
            color="orange"
          />
        </div>

        {/* Reports Summary */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-sm text-gray-500 mb-1">Reports (All Time)</div>
            <div className="text-xl font-bold text-gray-900">{overall?.total_reports_all_time?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-sm text-gray-500 mb-1">Reports (This Year)</div>
            <div className="text-xl font-bold text-gray-900">{overall?.total_reports_this_year?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-sm text-gray-500 mb-1">Reports (This Month)</div>
            <div className="text-xl font-bold text-gray-900">{overall?.total_reports_this_month?.toLocaleString() || 0}</div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* THIS WEEK'S STATUS */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span>📅</span> This Week's Status
          {thisWeek && (
            <span className="text-sm font-normal text-gray-400 ml-2">
              W{thisWeek.week_number.toString().padStart(2, '0')}-{thisWeek.year}
            </span>
          )}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Reports Submitted */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-gray-900">Reports Submitted</div>
              <span className="text-2xl">📝</span>
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {thisWeek?.reports_submitted || 0}
            </div>
            <div className="text-sm text-gray-500">
              by {thisWeek?.unique_reporters || 0} employees
            </div>
          </div>

          {/* Rollups Completed */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-gray-900">Rollups Completed</div>
              <span className="text-2xl">✅</span>
            </div>
            <div className="text-3xl font-bold text-green-600 mb-2">
              {thisWeek?.rollups_completed || 0}
            </div>
            {thisWeek?.managers_with_rollups && thisWeek.managers_with_rollups.length > 0 && (
              <div className="text-sm text-gray-500">
                {thisWeek.managers_with_rollups.slice(0, 3).join(', ')}
                {thisWeek.managers_with_rollups.length > 3 && ` +${thisWeek.managers_with_rollups.length - 3} more`}
              </div>
            )}
          </div>

          {/* Rollups Pending */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-gray-900">Rollups Pending</div>
              <span className="text-2xl">⏳</span>
            </div>
            <div className={`text-3xl font-bold mb-2 ${(thisWeek?.rollups_pending || 0) > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
              {thisWeek?.rollups_pending || 0}
            </div>
            {thisWeek?.managers_without_rollups && thisWeek.managers_without_rollups.length > 0 && (
              <div className="text-sm text-gray-500">
                {thisWeek.managers_without_rollups.slice(0, 3).join(', ')}
                {thisWeek.managers_without_rollups.length > 3 && ` +${thisWeek.managers_without_rollups.length - 3} more`}
              </div>
            )}
          </div>
        </div>

        {/* Week Info */}
        {thisWeek && (
          <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
            <strong>Week {thisWeek.week_number}:</strong>{' '}
            {new Date(thisWeek.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} –{' '}
            {new Date(thisWeek.week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            <span className="text-blue-500 ml-2">(Monday 08:00 UTC start)</span>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* QUICK ACTIONS */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span>⚡</span> Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <QuickAction href="/admin/employees" icon="👥" label="Manage Employees" />
          <QuickAction href="/admin/customers" icon="🏢" label="Manage Customers" />
          <QuickAction href="/admin/products" icon="📦" label="Manage Products" />
          <QuickAction href="/admin/import" icon="📤" label="Import Data" />
          <QuickAction href="/admin/simulator" icon="🧪" label="Test Rollups" />
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* SETUP CHECKLIST (for new orgs) */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {(overall?.total_employees === 0 || overall?.total_customers === 0 || overall?.total_products === 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-amber-800 mb-4 flex items-center gap-2">
            <span>🚀</span> Getting Started
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className={overall?.total_employees && overall.total_employees > 0 ? 'text-green-500' : 'text-gray-400'}>
                {overall?.total_employees && overall.total_employees > 0 ? '✅' : '○'}
              </span>
              <span className={overall?.total_employees && overall.total_employees > 0 ? 'text-gray-500 line-through' : 'text-gray-700'}>
                Import your employees
              </span>
              {(!overall?.total_employees || overall.total_employees === 0) && (
                <a href="/admin/import" className="text-sm text-blue-600 hover:underline ml-auto">
                  Import now →
                </a>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className={overall?.total_teams && overall.total_teams > 0 ? 'text-green-500' : 'text-gray-400'}>
                {overall?.total_teams && overall.total_teams > 0 ? '✅' : '○'}
              </span>
              <span className={overall?.total_teams && overall.total_teams > 0 ? 'text-gray-500 line-through' : 'text-gray-700'}>
                Set up teams and hierarchy
              </span>
              {(!overall?.total_teams || overall.total_teams === 0) && (
                <a href="/admin/teams" className="text-sm text-blue-600 hover:underline ml-auto">
                  Create teams →
                </a>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className={overall?.total_customers && overall.total_customers > 0 ? 'text-green-500' : 'text-gray-400'}>
                {overall?.total_customers && overall.total_customers > 0 ? '✅' : '○'}
              </span>
              <span className={overall?.total_customers && overall.total_customers > 0 ? 'text-gray-500 line-through' : 'text-gray-700'}>
                Add your customers
              </span>
              {(!overall?.total_customers || overall.total_customers === 0) && (
                <a href="/admin/customers" className="text-sm text-blue-600 hover:underline ml-auto">
                  Add customers →
                </a>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className={overall?.total_products && overall.total_products > 0 ? 'text-green-500' : 'text-gray-400'}>
                {overall?.total_products && overall.total_products > 0 ? '✅' : '○'}
              </span>
              <span className={overall?.total_products && overall.total_products > 0 ? 'text-gray-500 line-through' : 'text-gray-700'}>
                Add your products
              </span>
              {(!overall?.total_products || overall.total_products === 0) && (
                <a href="/admin/products" className="text-sm text-blue-600 hover:underline ml-auto">
                  Add products →
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
